import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useProduct } from "./product-context";
import { type Product } from "./product-context";
import { MARKETS, type MarketSymbol } from "@/solana/constants";
import { useMarkets } from "@/domains/markets";

const defaultMarketSymbol: MarketSymbol = "BTC-USD";

/**
 * ProductUrlSync - Syncs the URL market parameter with the product context
 * This component must be rendered inside a Route to access useParams
 */
export const ProductUrlSync = () => {
  const params = useParams<{ market: string }>();
  const navigate = useNavigate();
  const { markets } = useMarkets();
  const { product, setProduct } = useProduct();

  useEffect(() => {
    const marketSymbol = params.market?.toUpperCase();
    
    // Wait for markets to load
    if (!markets || markets.length === 0) {
      return;
    }

    // If no market in URL, redirect to default
    if (!marketSymbol) {
      navigate(`/trade/${defaultMarketSymbol}`, { replace: true });
      return;
    }

    // Skip if already set to this market
    if (product?.id === marketSymbol) {
      return;
    }

    // Find market in available markets (check both dynamic and hardcoded)
    let foundMarket = markets.find(m => m.symbol === marketSymbol);
    
    // If not found in dynamic markets, check hardcoded MARKETS
    if (!foundMarket && marketSymbol in MARKETS) {
      const hardcodedMarket = MARKETS[marketSymbol as MarketSymbol];
      foundMarket = hardcodedMarket as any;
    }
    
    if (!foundMarket) {
      // Invalid market - redirect to default silently
      navigate(`/trade/${defaultMarketSymbol}`, { replace: true });
      return;
    }

    // Set product from URL market
    const newProduct: Product = {
      id: foundMarket.symbol,
      symbol: foundMarket.symbol,
      base: {
        code: foundMarket.baseSymbol,
        precision: foundMarket.basePrecision,
        stepSize: foundMarket.minOrderSize?.toString() || "0.001",
      },
      quote: {
        code: foundMarket.quoteSymbol,
        precision: foundMarket.quotePrecision,
        tickSize: foundMarket.tickSize?.toString() || "0.01",
      },
      minOrderValueUsd: foundMarket.minOrderValueUsd || 10, // Default $10 if not set
    };

    setProduct(newProduct);
  }, [params.market, markets, navigate, product?.id, setProduct]);

  // This component doesn't render anything
  return null;
};

